import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles, Heart, Car, Baby, UtensilsCrossed, Star, Users, Zap, CheckCircle, Hotel, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import ExperienceCard from '../components/ExperienceCard';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  restaurang: UtensilsCrossed,
  wellness: Sparkles,
  traning: Heart,
  kultur: Star,
  hotell: Hotel,
  hem: Home,
};

const categoryImages = {
  restaurang: "/cat-restaurang.png",
  wellness: "/cat-wellness.jpg",
  traning: "/cat-traning.png",
  kultur: "/cat-kultur.png",
  hotell: "/cat-hotell.png",
  hem: "/cat-hem.png",
};

const carouselImages = [
  "/charcuterie-new.png",
  "/kettlebell-new.png",
  "/pool-lounge-hd.png",
  "/spa-poolside-hd.png",
  "/champagne-hd.png",
  "/sunset-hd.png",
  "/restaurant-hd.png",
  "/spa-girl.png",
];

const heroVideos = [
  "/hero-video-3.mp4",
  "/hero-video-1.mp4",
  "/hero-video-2.mp4",
];

export default function HomePage() {
  const { t, lang } = useLanguage();
  const [experiences, setExperiences] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentQuote, setCurrentQuote] = useState(0);
  const [currentVideo, setCurrentVideo] = useState(0);

  const nextVideo = useCallback(() => {
    setCurrentVideo(prev => (prev + 1) % heroVideos.length);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % carouselImages.length);
  }, []);

  const nextQuote = useCallback(() => {
    setCurrentQuote(prev => (prev + 1) % 4);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 3500);
    return () => clearInterval(timer);
  }, [nextSlide]);

  useEffect(() => {
    const timer = setInterval(nextQuote, 3000);
    return () => clearInterval(timer);
  }, [nextQuote]);

  useEffect(() => {
    const timer = setInterval(nextVideo, 8000);
    return () => clearInterval(timer);
  }, [nextVideo]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [expRes, catRes] = await Promise.all([
          axios.get(`${API}/experiences`),
          axios.get(`${API}/categories`)
        ]);
        setExperiences(expRes.data);
        setCategories(catRes.data);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#F4EDE6]" data-testid="home-page">
      {/* Hero Section - Video Carousel with Text */}
      <section className="bg-[#F4EDE6]">
        {/* Hero Video Carousel with Text Overlay */}
        <div className="relative w-full h-[85vh] md:h-[90vh] overflow-hidden">
          <AnimatePresence>
            {heroVideos.map((video, index) => (
              index === currentVideo && (
                <motion.video
                  key={index}
                  src={video}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 2.5, ease: 'easeInOut' }}
                />
              )
            ))}
          </AnimatePresence>
          {/* Dark overlay for better text readability */}
          <div className="absolute inset-0 bg-black/30" />
          
          {/* Text Content on Image */}
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto text-center px-6"
            >
              <h1 className="font-['Avenir_Next'] text-3xl md:text-5xl lg:text-6xl font-medium text-white leading-tight mb-6 drop-shadow-lg">
                {t('home.heroTitle')} {t('home.heroSubtitle')}
              </h1>
              <p className="text-[#F4EDE6] font-medium text-lg mb-10 drop-shadow-md">
                {t('home.heroTagline')}
              </p>

              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/upplevelser">
                  <Button className="bg-[#C4A77D] hover:bg-[#B39669] text-white h-14 px-8 text-base rounded-full" data-testid="hero-explore-button">
                    {t('home.exploreBtn')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/bli-medlem">
                  <Button className="bg-[#C4A77D] hover:bg-[#B39669] text-white h-14 px-8 text-base rounded-full" data-testid="hero-member-button">
                    <Users className="w-5 h-5 mr-2" />
                    {t('home.memberBtn')}
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section - Värdeerbjudande */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="font-['Avenir_Next'] text-3xl md:text-5xl text-[#1A1A1A] mb-6">
              {t('home.valueTitle')}
            </h2>
            <p className="text-xl text-[#595959] leading-relaxed mb-4">
              {t('home.valueDesc')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 md:py-24 bg-[#F4EDE6]">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="font-['Avenir_Next'] text-3xl md:text-4xl text-[#1A1A1A] mb-4">
              {t('home.categoriesTitle')}
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-4xl mx-auto">
            {categories.map((cat, i) => {
              const Icon = categoryIcons[cat.id] || Sparkles;
              const bgImage = categoryImages[cat.id];
              return (
                <motion.div
                  key={cat.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Link
                    to={`/upplevelser?category=${cat.id}`}
                    className={`block rounded-2xl text-center transition-all duration-300 group card-hover overflow-hidden relative ${
                      bgImage ? 'h-48' : 'p-6 bg-white hover:bg-[#C4A77D] hover:text-white'
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    {bgImage ? (
                      <>
                        <img src={bgImage} alt={cat.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors" />
                        <div className="relative z-10 h-full flex flex-col items-center justify-center">
                          <div className="w-14 h-14 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                            <Icon className="w-7 h-7 text-white" />
                          </div>
                          <h3 className="font-medium text-white mb-1">{t(`categories.${cat.id}`) || cat.name}</h3>
                          <p className="text-sm text-white/70">{cat.count} {t('home.experiences')}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="w-14 h-14 mx-auto mb-4 bg-[#C4A77D]/10 group-hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
                          <Icon className="w-7 h-7 text-[#C4A77D] group-hover:text-white" />
                        </div>
                        <h3 className="font-medium mb-1">{t(`categories.${cat.id}`) || cat.name}</h3>
                        <p className="text-sm text-[#8C8C8C] group-hover:text-white/70">
                          {cat.count} {t('home.experiences')}
                        </p>
                      </>
                    )}
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section - Hur det fungerar */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-['Avenir_Next'] text-3xl md:text-5xl text-[#1A1A1A] mb-6">
              {t('home.howTitle')}
            </h2>
            <p className="text-xl text-[#595959] max-w-3xl mx-auto">
              {t('home.howSubtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Users, title: t('home.step1Title'), desc: t('home.step1Desc'), image: "/step-bli-medlem.png", href: "/bli-medlem" },
              { icon: Sparkles, title: t('home.step2Title'), desc: t('home.step2Desc'), image: "/step-utforska.png", href: "/upplevelser" },
              { icon: Star, title: t('home.step3Title'), desc: t('home.step3Desc'), image: "/step-samla-poang.png", href: "/kundservice" }
            ].map((step, i) => (
              <Link key={i} to={step.href}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  className="relative rounded-2xl overflow-hidden h-64 group cursor-pointer"
                >
                  <img src={step.image} alt={step.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors" />
                  <div className="relative z-10 h-full flex flex-col items-center justify-center text-center p-8">
                    <div className="w-16 h-16 mx-auto mb-6 bg-white/20 rounded-full flex items-center justify-center">
                      <step.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-['Avenir_Next'] text-2xl mb-3 text-white">{step.title}</h3>
                    <p className="text-white/80">{step.desc}</p>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Section - Inkludering */}
      <section className="py-20 md:py-28 bg-[#C4A77D]">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center text-white"
          >
            <h2 className="font-['Avenir_Next'] text-3xl md:text-5xl mb-6">
              {t('home.forAllTitle')}
            </h2>
            <p className="text-xl leading-relaxed mb-4 text-white/90">
              {t('home.forAllDesc')}
            </p>
            <div className="mt-12">
              <p className="font-['Avenir_Next'] text-2xl md:text-3xl italic">
                {t('home.forAllQuote')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Experiences */}
      <section className="py-16 md:py-24 bg-[#F4EDE6]">
        <div className="container-main">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
            <div>
              <h2 className="font-['Avenir_Next'] text-3xl md:text-4xl text-[#1A1A1A] mb-2">
                {t('home.popularTitle')}
              </h2>
              <p className="text-[#595959]">{t('home.popularSubtitle')}</p>
            </div>
            <Link to="/upplevelser">
              <Button variant="outline" className="btn-secondary">
                {t('home.showAll')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-xl overflow-hidden">
                  <div className="skeleton h-56" />
                  <div className="p-5 space-y-3">
                    <div className="skeleton h-4 w-20" />
                    <div className="skeleton h-6 w-3/4" />
                    <div className="skeleton h-4 w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {experiences.slice(0, 6).map((exp, i) => (
                <ExperienceCard key={exp.id} experience={exp} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Section - Enkelhet */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-['Avenir_Next'] text-3xl md:text-5xl text-[#1A1A1A] mb-6">
                {t('home.simpleTitle')}
              </h2>
              <p className="text-xl text-[#595959] leading-relaxed mb-8">
                {t('home.simpleDesc')}
              </p>
              <ul className="space-y-4">
                {[
                  t('home.simplePoint1'),
                  t('home.simplePoint2'),
                  t('home.simplePoint3')
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#595959]">
                    <CheckCircle className="w-5 h-5 text-[#C4A77D]" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <img
                src="/spa-poolside-hd.png"
                alt="Spa"
                className="rounded-2xl h-48 md:h-64 object-cover w-full"
              />
              <img
                src="/hotel-room-hd.png"
                alt="Hotell"
                className="rounded-2xl h-48 md:h-64 object-cover w-full mt-8"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section - Maximera medlemskapet */}
      <section className="py-20 md:py-28 bg-[#1A1A1A]">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="font-['Avenir_Next'] text-3xl md:text-5xl mb-6">
              {t('home.maxTitle')}
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto mb-4">
              {t('home.maxDesc')}
            </p>
            <p className="text-lg text-[#C4A77D] mb-12">
              {t('home.maxTagline')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/upplevelser">
                <Button className="bg-[#C4A77D] hover:bg-[#B39669] text-white h-14 px-8 text-base rounded-full">
                  {t('home.maxExploreBtn')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/bli-medlem">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 h-14 px-8 text-base rounded-full">
                  <Users className="w-5 h-5 mr-2" />
                  {t('home.maxMemberBtn')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Membership Promo */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-[#C4A77D] to-[#D4B896]">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-white"
            >
              <Users className="w-16 h-16 mb-6 opacity-80" />
              <h2 className="font-['Avenir_Next'] text-3xl md:text-4xl mb-4">
                {t('home.memberPromoTitle')}
              </h2>
              <p className="text-xl text-white/90 mb-4">
                {t('home.memberPromoDesc')}
              </p>
              <p className="text-white/80 italic mb-6">
                {t('membership.whatTagline')}
              </p>
              <Link to="/bli-medlem">
                <Button className="bg-white text-[#C4A77D] hover:bg-white/90 h-14 px-8 text-base rounded-full">
                  {t('home.memberPromoBtn')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="hidden lg:block"
            >
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="/pool-lounge-hd.png"
                  alt="Pool"
                  className="rounded-2xl h-48 object-cover w-full"
                />
                <img
                  src="/food-toasts-hd.png"
                  alt="Mat"
                  className="rounded-2xl h-48 object-cover w-full mt-8"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bottom Banner - Logo Image Carousel */}
      <section className="relative w-full overflow-hidden bg-[#1A1A1A]" style={{ height: '300px' }} data-testid="logo-carousel">
        <AnimatePresence initial={false}>
          <motion.img
            key={currentSlide}
            src={carouselImages[currentSlide]}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ x: '100%', opacity: 0.5 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '-100%', opacity: 0.5 }}
            transition={{ duration: 0.8, ease: 'easeInOut' }}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-black/55 z-[1]" />
        <div className="absolute inset-0 flex items-center justify-center z-[2]">
          <div className="flex flex-col items-center gap-3">
            <div className="h-20 md:h-24 lg:h-28 overflow-hidden">
              <img 
                src="/vardagslyx-hero-logo.png"
                alt=""
                className="h-36 md:h-44 lg:h-52 w-auto object-contain object-top"
                style={{ mixBlendMode: 'screen', filter: 'brightness(10)' }}
              />
            </div>
            <span className="font-['Avenir_Next'] text-white text-3xl md:text-4xl lg:text-5xl tracking-[0.2em] font-light drop-shadow-lg">
              VARDAGSLYX
            </span>
          </div>
        </div>
      </section>

    </div>
  );
}
